/**
 * バトルのバランス値 (BATTLE_TUNING)。
 * (battle.ts から責務ごとに分割。公開 API は battle.ts から再エクスポート)
 */

// ─── チューニング ───────────────────────────────────────────

export const BATTLE_TUNING = {
  /** 1 戦のあおぞらパワー消費 */
  powerCost: 1,
  /** 勝利 XP のフォールバック既定値。通常は各モンスターの `xp` (battleXpFor) を使う。
   *  未知モンスター id のときだけこの値。 */
  xpWin: 30,
  /**
   * 敗北 XP。**0 = 負けても経験値は入らない** (#621)。
   *
   * 以前は 5 を配っていたが、tier1 のスライム 3 種は勝利 XP が 3〜5 しかなく
   * **負けたほうが多い (または同じ)** という逆転が実在した (実測)。序盤ほど遭遇する
   * 敵なので踏みやすい。強い敵ほど勝利 XP が上がる設計と、固定額の敗北 XP は
   * 原理的に噛み合わない (敵を弱く足すたびに再発する) ため、配るのをやめる。
   * 負けたときは素材ロスとパワー消費だけが残る。
   */
  xpLose: 0,
  /** HP = hpBase + def × hpDefScale × ジョブ Lv 係数 (#507)。
   *  DQ 級の小さいスケール (敵ひとけた) にして、レベルアップ/装備の +数の恩恵を体感させる
   *  (小さい母数ほど +2 が効く)。
   *
   *  **hpDefScale はジョブごとの HP 伸び率そのもの** (#507): HP が def に比例するので、def43 の
   *  守護者は def15 の賢者より素で硬く、ジョブ Lv が上がるほど差が開く (= 職の個性が HP に出る)。
   *  旧式は `+ プレイヤーLv × hpLevelScale` が支配項で、(a) 職差がほぼ無い (b) ジョブ Lv で伸びない
   *  (c) プレイヤー Lv で同じ職の HP が変わる、を同時に起こしていたので撤廃した。 */
  hpBase: 6,
  /** HP = hpBase + たいりょく × hpVitScale (#518。DQ 準拠「体力の約 2 倍まで HP が上昇」)。
   *  たいりょく自体が statBase/statGrow でレベル成長するので、**HP も毎レベル +2〜4 動く**。 */
  hpVitScale: 2,
  /** 敵の HP/MP に遭遇ごとの分散 (±この割合)。値を毎回固定にせず、「あと何回
   *  使えるか」をプレイヤーに予想させる。seed 決定的。
   *  world 遭遇のみ適用し、バランステスト対象の trial は 0 (固定) に保つ。 */
  monsterVitalsVariance: 0.15,
  /** レベルによるステータス補正 = 1 + (jobLv-1) × jobLevelScale。
   *  **成長軸はジョブ Lv のみ** (#507)。旧 playerLevelScale / flatLevelGain (プレイヤー Lv 由来) は
   *  撤廃した — 同じ職・同じジョブ Lv でもプレイヤー Lv で強さが変わり、「その職の強さ」が
   *  定まらなかったため。 */
  jobLevelScale: 0.04,
  /** ステータス = 比率 × (statBase + statGrow × (jobLv−1))  (#518)。
   *  **比率 (合計 100 の職プロファイル) は「伸び率」に掛ける**。以前は比率をそのまま Lv1 の絶対値に
   *  使っていたため「大きく始まってほとんど伸びない」(Lv1 int40 → Lv50 118 の 3 倍) 状態だった。
   *  DQ は小さく始まって 15〜20 倍に伸びるので、それに寄せた (Lv1 int8 → Lv50 106 の 13 倍)。 */
  statBase: 0.20,
  /** 全ステータス共通の下駄 (#518)。
   *
   *  職の比率は atk だけで 7 (魔法使い) 〜 39 (将軍) と 5.6 倍の開きがある。これを Lv1 で
   *  そのまま比率 × 0.20 にすると atk 1 対 8 になり、**減算式 (atk*0.9 − def*0.45) が
   *  低い側で 0 に潰れて何をしても 1 も通らない**。DQ の Lv1 も単純な数値だが、勇者の
   *  ちから 4 + 武器 のように**全員が一定の底上げ**を持つので潰れない。それを再現する。
   *
   *  Lv50 では比率 × 2.65 が 20〜103 になるので下駄は誤差に薄まる = 職差は保たれる。
   *  **まもり (def) には掛けない** — 守備は防具が主役という設計 (defBase 参照) を、
   *  下駄で薄めてしまわないため。モンスターは防具を持たないので 5 ステとも掛ける。 */
  statFloor: 5,
  /** モンスター専用の下駄 (#536)。プレイヤーの `statFloor` (5) をそのまま使うと、
   *  **tier1 では実効値がほぼ下駄だけになる** (raw 比率 × 0.072 = 0.5〜1.6 しかないため)。
   *  結果 tier1 の敵が全部 `atk6 def6` に横並びし、**Lv1 の低 HP 職が 1 戦もたない**
   *  (魔法使い HP14 に対して 1 戦の消耗が 15〜25) = 「最初から強すぎる」の実体。
   *
   *  モンスターは職と違って「比率の合計」が敵ごとに大きく違う (tier1 は 38〜71、
   *  tier3 は 126〜136) ので、下駄を小さくしても 0 に潰れない。入口を緩めつつ
   *  敵ごとの個性 (硬い/素早い) が出るようにする。
   *
   *  値は「街を出てから何戦できるか」(街に入ると全回復) の実測で決める。
   *  再現は `pnpm --filter @aozoraquest/core sim:endurance` (scripts/sim-endurance.mts)。
   *
   *  | 下駄 | tier1 Lv1 裸 | tier2 Lv5 布の服 | tier3 Lv9 旅の外套 |
   *  |---|---|---|---|
   *  | 6 | 2.25 戦 | — | — |
   *  | 4 | 4.65 戦 | 13.99 戦 | 5.84 戦 |
   *  | **3 (現在)** | **7.24 戦** | 17.81 戦 | 7.87 戦 |
   *
   *  3 を採るのは、入口 (7.24) と中盤 (7.87) が揃うから。4 だと入口 4.65 に対し
   *  tier3 が 5.84 で「入口が一番きつい」逆転が残る。
   *
   *  上の表は dev と同一ハーネスで測ったもの。 */
  monsterStatFloor: 4.3,
  statGrow: 0.05,
  /** まもりだけ伸びを抑える (#518)。**守備力は防具が主役**という DQ の構造に寄せるため
   *  (FC版〜:「すばやさ÷2 + 防具」/ DQ3 HD-2D〜:「みのまもり + 防具」— いずれも防具が大きい)。
   *  素のまもりが小さいので、HP (たいりょく) と守備の二重取りが起きない。 */
  defBase: 0.06,
  defGrow: 0.015,
  /** 個人 rpgStats とジョブ基準値のブレンド比 (0 = ジョブのみ / 1 = 個人のみ)。
   *  診断は min-max 正規化で全員が極端プロフィールになるため、個人値 100% だと
   *  atk 0〜80 のレンジになり勝率が 0〜100% に割れる (issue #279 実測)。
   *  50:50 でジョブの型を保ちつつ個人の傾きを乗せる。 */
  baseStatsPersonalWeight: 0.5,
  /** ダメージ = max(minDamage, (atk*atkCoef*power − def*defCoef) * roll(0.85..1.15))。DQ の減算式
   *  (攻撃÷2 − 防御÷4) 流。**防御の係数を攻撃の半分 (2:1)** にしてインフレを抑える。会心は def 項 0
   *  (守備無視)。数値は sim で調整。 */
  atkCoef: 0.9,
  defCoef: 0.45,
  /** **0 = 守備力を上回れなければ 1 も通らない**。
   *  以前は 1 で「必ず最低 1 は通る」仕様だったが、これはメタル系の identity
   *  (「かいしんのいちげき か 特殊武器 でしか倒せない」) を壊す読み違いだった。
   *  0 にすることで高守備の敵は専用ロジックなしに「通常攻撃が効かない」を表現できる。 */
  minDamage: 0,
  /** **かすりダメージ**: 減算の結果が 0 に沈んだとき、`攻撃力 × この比率` を最低保証する
   *  (`ironDef` = メタル系だけは対象外で、従来どおり完全に 0)。
   *
   *  なぜ要るか: minDamage=0 はメタル系のためのものだったが、**プレイヤー側の守備でも
   *  同じ崖に届いてしまう**。grade2 の防具 1 点 (def+15) で Lv5 の守備は 2 → 18 になり、
   *  tier1 のモンスターは実効 atk が 5〜6 = `6×0.9 = 5.4 < 18×0.45 = 8.1` で **1 も通らない**。
   *  実測で tier1 は被ダメ 0・無傷率 100%・連戦は打ち切りまで無敗になっていた
     *  「硬い相手には通らない」は敵の identity として設計したものであって、
   *  **プレイヤーが不死身になってよいという意味ではない**。
   *
   *  0.25 の根拠 (装備 + レベルアップ全回復ありで再導出。全職平均の連戦数):
   *
   *  | ratio | 素手 t1 | 素手 t2 | ローブ t1 | ローブ t2 | ローブ t3 |
   *  |---|---|---|---|---|---|
   *  | 0.00 (旧) | 39.7 | 5.0 | 224 | 47.6 | 0.9 |
   *  | 0.10 | 39.7 | 5.0 | 211 | 26.2 | 0.9 |
   *  | **0.25** | **39.7** | **5.0** | **148** | **7.0** | **0.9** |
   *  | 0.30 | 39.7 | 5.0 | 80 | 5.9 | 0.9 |
   *
   *  **素手と tier3 は ratio を動かしても 1 も変わらない** — 減算が既に正なので床が効かない。
   *  つまりこの値は「装備した低 tier」だけに効く。0.25 で装備 tier2 (7.0) が素手 tier2 (5.0) に
   *  並ぶところまで締まり、かつ装備した意味は残る。0.30 まで上げると装備の価値が消える。 */
  scratchRatio: 0.25,
  /** 回避率 = clamp(base + (守る側agi - 攻める側agi)*agiDodgeScale, min, max) */
  dodgeBase: 0.04,
  agiDodgeScale: 0.009,
  dodgeMin: 0.02,
  // focus 込みの実効上限は dodgeMax + guardFocusDodge (ぼうぎょ直後の高 agi 職で最大 0.47)
  dodgeMax: 0.32,
  /** クリティカル率 = critBase + luk*critLukScale。会心 (かいしんのいちげき) は DQ 流に
   *  **攻撃力 critAtkMultiplier 倍**。守備力 (def) 無視は**プレイヤーの会心のみ** (敵の会心は
   *  1.5 倍のみ = タンク職を守る。バランス ★★★)。倍率は控えめ 1.5 (2 は高すぎ
   *  2026-07-20)。ただし**減算式では会心の守備無視は def 項 (def*defCoef) を消すだけ**なので、
   *  除算式時代 (分母消滅で桁が変わる) ほど劇的ではない = 守備の高い敵には「効くが一撃逆転
   *  までは行かない」控えめな貫通。超高守備メタルの一撃逆転演出は #408 で会心倍率と併せ再評価。
   *  ぼうぎょコマンドの半減は貫通しない。値は模擬戦シミュレータで調整可。 */
  critBase: 0.04,
  critLukScale: 0.004,
  /** MP 特性の発動確率に効く うん の係数と上限 (`MpTrait.chance` が基準値)。
   *  うんの高い職ほど「祈りが通じやすい」= 装備でうんを盛る意味が MP にも出る。
   *
   *  **上限に早く届かせない。** 0.004 / 上限 0.8 だと パラディンは裸 Lv38・専用武器込みなら
   *  Lv28 で頭打ちになり、そこから先は うん を +17 盛っても確率が 1 も動かなくなる
   *  (= 装備を選ぶ動機が消える帯ができる)。0.003 / 上限 0.85 なら、到達しうる最大
   *  (Lv50 × 最良装備 +10 = うん 119) でちょうど 0.85 に触れる = 全域で うん が効く。 */
  mpTraitLukScale: 0.003,
  mpTraitChanceMax: 0.85,
  critAtkMultiplier: 1.5,
  /** ぼうぎょ: 被ダメージ半減 */
  guardReduction: 0.5,
  /** ため攻撃 (tier2+ が 1 ターン予告してから放つ) の倍率。予告を見て防御するのが正解
   *  (2.6 → 防御で 1.3 まで軽減 = 節約 1.3 発分 > 機会費用の自攻撃 1 発分)。
   *  防御に存在意義を与える読み合いの核。バランステストで「予告に防御 > attack 連打」を固定。 */
  chargedPower: 2.6,
  /** charger が「ため」を宣言する確率 (ため中でないとき毎ターン判定)。 */
  chargerChargeChance: 0.4,
  /** healer が自己回復する条件と量。HP がこの割合を下回ると healChance で回復し、
   *  maxHp の healRatio ぶん戻す (削り切る前に倒す読み合い。無限回復にはしない)。 */
  healerLowHpRatio: 0.55,
  healerHealChance: 0.5,
  healerHealRatio: 0.14,
  /** caster が魔法を撃つ確率 (MP があるとき毎ターン判定)。残りは通常攻撃。数値は sim 前提の暫定値
   *  (def 無視の魔法は def タンクに刺さるため、初期投入は控えめ。full 調整は #479 sim)。 */
  casterCastChance: 0.3,
  /** caster の魔法 MP コスト。 */
  monsterCastMpCost: 6,
  /** モンスターの特技 MP コスト。MP は int から算出 (fromStats) 済み。ため/回復を
   *  MP 制にすることで「int の高い敵ほど特技を多用でき、尽きたら通常攻撃に落ちる」
   *  = MP を削り切る/尽きるのを待つ読み合いを作る。 */
  monsterChargeMpCost: 5,
  monsterHealMpCost: 7,
  /** debuffer の弱体・自分の強化の MP コスト (D-PARTY-002 §12a)。 */
  monsterDebuffMpCost: 5,
  /** debuffer が技を使う確率 (MP があり、まだ付いていない状態があるとき)。 */
  debufferChance: 0.3,
  /** 全体 (target 'all') の呪文・ため・弱体の MP は単体の何倍か (§12a: 全体は単体より重い)。 */
  monsterAoeMpFactor: 2,
  /** MP: 特技のコスト。最大 MP = mpBase + int * mpIntScale (int 職は手数が多い)。
   *  戦闘中の MP 回復は **ジョブ特性 (JOB_MP_TRAITS) を持つジョブだけ** —
   *  全員一律の回復はジョブの差をぼやけさせる。
   *  特性なしジョブは MP プール + そらのしずくでやりくりする。 */
  skillMpCost: 4,
  /** heal とくぎ (#436): 使うと maxHp のこの割合を回復 (MP 制。やくそうと違い在庫でなく MP を消費)。 */
  skillHealRatio: 0.35,
  mpBase: 3,
  /** MP = mpBase + かしこさ × mpIntScale (#518。DQ 準拠「かしこさが高いほど MP 上昇量が上がる」)。 */
  mpIntScale: 1,
  mpAttackGain: 0,
  mpGuardGain: 0,
  /** ぼうぎょの翌ターン回避ボーナス (身構えて相手の動きを読む)。 */
  guardFocusDodge: 0.15,
  /** やくそう: 使うと maxHp のこの割合を回復 (1 ターン消費)。持ち込み上限 herbCarryMax。
   *  0.4/3 個ではガード+薬草の tier3 勝率が 97% まで上がり真剣勝負が作業化した
   *  (レビューのシミュレーション実測) ため 0.3/2 個に抑制。天井はテストで固定。 */
  herbHealRatio: 0.3,
  herbCarryMax: 2,
  /** そらのしずく (MP 回復薬): maxMp のこの割合を回復。持ち込み上限 tonicCarryMax。 */
  tonicMpRatio: 0.5,
  tonicCarryMax: 2,
  /** 戦闘用のどうぐ (D-ITEM-001): 1 戦に種類ごとこの数まで持ち込める (やくそうと同じ上限)。 */
  battleItemCarryMax: 2,
  /** にげる: 成功率 = clamp(fleeBase + (自agi − 敵agi) * fleeAgiScale, min, max)。
   *  失敗するとターンを失い敵の攻撃を受ける。成功してもパワーは返さない。 */
  fleeBase: 0.5,
  fleeAgiScale: 0.012,
  fleeMin: 0.25,
  fleeMax: 0.95,
  /** モンスターの逃走 (ability='fleer')。毎ターン flee 判定する。逃走率 =
   *  clamp(monsterFleeBase + 自agi*monsterFleeAgiScale, 0, monsterFleeMax)。
   *  はぐれメタル型 (高XP・低HP・すぐ逃げる) 用: 倒す前に逃げられると報酬ゼロ。 */
  monsterFleeBase: 0.35,
  monsterFleeAgiScale: 0.006,
  monsterFleeMax: 0.75,
  /** ドロップ率の luk ボーナス = luk * dropLukScale (加算) */
  dropLukScale: 0.003,
  /** 敗北ペナルティ: 手持ち素材をランダムに落とす。
   *  1 個は必ず落ち、以降は lossExtraBase − luk*lossExtraLukScale の確率で
   *  追加ドロップ (運が悪いと複数落ちる)。上限 lossMax。 */
  lossExtraBase: 0.35,
  lossExtraLukScale: 0.004,
  lossExtraMin: 0.05,
  lossMax: 3,
  /** 群れの確率 (D-PARTY-001 §4)。tier ごとの [2 体, 3 体]。t3 以上は 3 の値。敵は全員本来の強さ。
   *  1 人でも本筋 (t1〜t2) は勝率の最低 91% (scripts/sim-party.mts)。t3 は「仲間を連れてくる場所」。 */
  groupChance: { 1: [0.08, 0], 2: [0.15, 0.03], 3: [0.25, 0.1] } as Record<1 | 2 | 3, readonly [number, number]>,
} as const;
